import type { To } from 'react-router';
import { merge } from 'lodash-es';

import type { AlignTypes } from '../shared/Align';
import type { FontSizeProp } from '../shared/FontSize';
import type { JustifyTypes } from '../shared/Justify';
import type { LineHeightProp } from '../shared/LineHeight';
import type { WidthTypes } from '../shared/Width';
import type { WrapTypes } from '../shared/Wrap';
import { styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { CaptionSansSizes } from '../../text/Caption';
import { HeadingSansSizes } from '../../text/Heading';
import { LargeSansSizes } from '../../text/Large';
import { SmallSansSizes } from '../../text/Small';
import { SubheadingSansSizes } from '../../text/Subheading';
import { TitleSansSizes } from '../../text/Title';
import { alignCSS } from '../shared/Align';
import { fontSizeCSS } from '../shared/FontSize';
import { justifyCSS } from '../shared/Justify';
import { lineHeightCSS } from '../shared/LineHeight';
import { marginCSS } from '../shared/Margin';
import { maxWidthCSS } from '../shared/MaxWidth';
import { type SpacingScale } from '../shared/sizes';
import { spacingCSS } from '../shared/Spacing';
import { widthCSS } from '../shared/Width';

const AlignStackLocked = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    direction: {
      column: {},
      'column-reverse': {},
      row: {
        heightAll: '1lh',
      },
      'row-reverse': {
        heightAll: '1lh',
      },
    },
    boxBound: {
      true: {
        widthAll: '1lh',
      },
    },
    shrinks: {
      true: {
        minWidth: 0,

        '& > *': {
          minWidth: 0,
        },
      },
      false: {},
    },
  },
});
AlignStackLocked.displayName = 'AlignStackLocked';

export const AlignStackElements = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexGrow: 1,
  minWidth: 0,

  variants: {
    truncate: {
      true: {
        maxWidth: '100%',
        overflow: 'hidden',

        '& > *': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
      false: {},
    },
    shrink: {
      elements: {},
      end: {
        flexShrink: 0,
        whiteSpace: 'nowrap',
      },
    },
  },
});
AlignStackElements.displayName = 'AlignStackElements';

const AlignStackContainer = styled('div', {
  display: 'flex',
  width: '100%',
  minWidth: 0,

  variants: {
    direction: {
      column: {
        flexDirection: 'column',
      },
      'column-reverse': {
        flexDirection: 'column-reverse',
      },
      row: {
        flexDirection: 'row',
      },
      'row-reverse': {
        flexDirection: 'row-reverse',
      },
    },
    wrap: {
      nowrap: {
        flexWrap: 'nowrap',
      },
      wrap: {
        flexWrap: 'wrap',
      },
      'wrap-reverse': {
        flexWrap: 'wrap-reverse',
      },
    },
    preset: {
      body: {
        ...BodySansSizes,
      },
      caption: {
        ...CaptionSansSizes,
      },
      heading: {
        ...HeadingSansSizes,
      },
      large: {
        ...LargeSansSizes,
      },
      small: {
        ...SmallSansSizes,
      },
      subheading: {
        ...SubheadingSansSizes,
      },
      title: {
        ...TitleSansSizes,
      },
    },
    truncate: {
      true: {
        maxWidth: '100%',
      },
      false: {},
    },
  },
});
AlignStackContainer.displayName = 'AlignStackContainer';

type AlignStackDirectionProp = 'column' | 'column-reverse' | 'row' | 'row-reverse';
export type AlignStackPresetProp =
  | 'body'
  | 'caption'
  | 'heading'
  | 'large'
  | 'small'
  | 'subheading'
  | 'title';

export type AlignStackProps = {
  align?: AlignTypes;
  boxBound?: boolean;
  direction?: AlignStackDirectionProp;
  children?: React.ReactNode;
  end?: React.ReactNode;
  fontSize?: FontSizeProp;
  gap?:
    | SpacingScale
    | {
        start?: SpacingScale;
        end?: SpacingScale;
        internal?: SpacingScale;
      };
  justify?: JustifyTypes;
  lineHeight?: LineHeightProp;
  start?: React.ReactNode;
  preset?: AlignStackPresetProp;
  /** Which part gives up space when the stack is too narrow: `elements` (default) or `end`. */
  shrink?: 'elements' | 'end';
  truncate?: boolean;
  width?: WidthTypes;
  maxWidth?: WidthTypes;
  wrap?: WrapTypes;
  // Routing
  as?: React.ElementType;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  to?: To;
  tabIndex?: number;
  ref?: React.RefObject<HTMLElement>;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'>;

const getAlign = (align: AlignTypes | undefined, direction: AlignStackDirectionProp) => {
  if (!align) {
    if (direction === 'row' || direction === 'row-reverse') {
      return 'start';
    }
    return 'center';
  }
  return align;
};

const getJustify = (justify: JustifyTypes | undefined, direction: AlignStackDirectionProp) => {
  if (!justify) {
    if (direction === 'row' || direction === 'row-reverse') {
      return 'start';
    }
    return 'center';
  }
  return justify;
};

const getStartGap = (gap: SpacingScale, direction: AlignStackDirectionProp) => {
  if (direction === 'row-reverse') {
    return { left: gap };
  }
  if (direction === 'column') {
    return { bottom: gap };
  }
  if (direction === 'column-reverse') {
    return { top: gap };
  }
  return { right: gap };
};

const getEndGap = (gap: SpacingScale, direction: AlignStackDirectionProp) => {
  if (direction === 'row-reverse') {
    return { right: gap };
  }
  if (direction === 'column') {
    return { top: gap };
  }
  if (direction === 'column-reverse') {
    return { bottom: gap };
  }
  return { left: gap };
};

export function AlignStack({
  align,
  boxBound = false,
  direction = 'row',
  children,
  end,
  fontSize,
  gap,
  justify,
  lineHeight,
  start,
  preset,
  shrink = 'elements',
  truncate = false,
  width,
  maxWidth,
  wrap,
  // Routing
  as: Component = 'div',
  onClick,
  to,
  tabIndex,
  ref,
  ...htmlAttributes
}: AlignStackProps) {
  const hasGapObject = gap && typeof gap === 'object';

  const containerCSS = merge(
    alignCSS(getAlign(align, direction)),
    justifyCSS(getJustify(justify, direction)),
    spacingCSS(hasGapObject ? undefined : gap),
    widthCSS(width),
    maxWidthCSS(maxWidth),
    !preset ? fontSizeCSS(fontSize) : undefined,
    !preset ? lineHeightCSS(lineHeight) : undefined,
  );

  const lockedStartCSS =
    hasGapObject && gap.start ? merge(marginCSS(getStartGap(gap.start, direction))) : undefined;
  const lockedEndCSS =
    hasGapObject && gap.end ? merge(marginCSS(getEndGap(gap.end, direction))) : undefined;

  const elementsCSS = merge(
    alignCSS(getAlign(align, direction)),
    justifyCSS(getJustify(justify, direction)),
    spacingCSS(hasGapObject && gap.internal ? gap.internal : undefined),
  );

  return (
    <AlignStackContainer
      as={Component}
      to={to}
      onClick={onClick}
      tabIndex={tabIndex ?? (Component === 'button' || to || onClick ? 0 : undefined)}
      type={Component === 'button' ? 'button' : undefined}
      css={containerCSS}
      direction={direction}
      wrap={wrap}
      preset={preset}
      ref={ref}
      truncate={truncate}
      {...htmlAttributes}
    >
      {start && (
        <AlignStackLocked boxBound={boxBound} direction={direction} css={lockedStartCSS}>
          {start}
        </AlignStackLocked>
      )}
      {children && (
        <AlignStackElements css={elementsCSS} shrink={shrink} truncate={truncate}>
          {/* Wrap string children in span so truncation works. */}
          {truncate && typeof children === 'string' ? <span>{children}</span> : children}
        </AlignStackElements>
      )}
      {end && (
        <AlignStackLocked
          boxBound={boxBound}
          direction={direction}
          css={lockedEndCSS}
          shrinks={shrink === 'end'}
        >
          {end}
        </AlignStackLocked>
      )}
    </AlignStackContainer>
  );
}
